import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import TintedIcon from '@/components/ui/TintedIcon/TintedIcon';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { ManagedUserDtoIn } from '@/models/in/ManagedUserDtoIn';
import Fund from '@/models/Fund';
import editIcon from '@/assets/icons/edit-pencil.svg';
import permissionsIcon from '@/assets/icons/permissions.svg';
import loginAsIcon from '@/assets/icons/login-as.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import CompactDialog from './CompactDialog';
import DeleteUserDialog from './DeleteUserDialog';
import EditUserDetailsDialog from './EditUserDetailsDialog';
import EditUserPermissionsDialog from './EditUserPermissionsDialog';
import UserBlade from './UserBlade';
import UsersFiltersArea from './UsersFiltersArea';
import RoleChips from './RoleChips';
import useTableDensity from '@/hooks/useTableDensity';
import {
  ActivityFilter,
  ASSIGNABLE_ROLES,
  formatUserDate,
  getUserFullName,
  hasRole,
  matchesActivityFilter,
  matchesSearch,
  NO_ROLE_FILTER_ID,
  roleSortValue,
  userDateSortValue,
} from './userHelpers';
import styles from './UsersManagementPage.module.css';

const USERS_URL = 'users';
const DENSITY_STORAGE_KEY = 'table.density.usersManagement';

/** How many fund codes fit in the table cell before the rest collapse into a "+N" chip. */
const MAX_VISIBLE_FUNDS = 2;

const UsersManagementPage = () => {
  const { t } = useTranslation();
  const { currentUser, isCurrentUserAdmin, loginAs } = useAuthContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');

  const [selectedUser, setSelectedUser] = useState<ManagedUserDtoIn | null>(null);
  const [userToEdit, setUserToEdit] = useState<ManagedUserDtoIn | null>(null);
  const [userToEditPermissions, setUserToEditPermissions] = useState<ManagedUserDtoIn | null>(null);
  const [userToLoginAs, setUserToLoginAs] = useState<ManagedUserDtoIn | null>(null);
  const [userToDelete, setUserToDelete] = useState<ManagedUserDtoIn | null>(null);
  const [isLoggingInAsUser, setIsLoggingInAsUser] = useState(false);
  const [loginAsError, setLoginAsError] = useState<string | null>(null);

  const { data: users, isLoading, refetch } = useGetAuthApi<ManagedUserDtoIn[]>(USERS_URL);
  const { data: funds } = useGetAuthApi<Fund[]>('funds');

  // Everyone in the management area may look at the list, but only admins may change anything -
  // the matching endpoints are admin-only on the server as well.
  const canManage = isCurrentUserAdmin();

  const fundNamesById = useMemo(() => {
    const map = new Map<number, string>();
    (funds ?? []).forEach((fund) => map.set(fund.id, fund.name));
    return map;
  }, [funds]);

  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = { [NO_ROLE_FILTER_ID]: 0 };
    ASSIGNABLE_ROLES.forEach((role) => (counts[role] = 0));

    (users ?? []).forEach((user) => {
      if ((user.roles ?? []).length === 0) counts[NO_ROLE_FILTER_ID] += 1;
      ASSIGNABLE_ROLES.forEach((role) => {
        if (hasRole(user, role)) counts[role] += 1;
      });
    });

    return counts;
  }, [users]);

  const filteredUsers = useMemo(() => {
    return (users ?? []).filter((user) => {
      const roles = user.roles ?? [];
      const matchesRole = selectedRoles.length === 0 || selectedRoles.some((role) => (role === NO_ROLE_FILTER_ID ? roles.length === 0 : roles.includes(role)));
      const matchesFund = selectedFundId === null || (user.fundIds ?? []).includes(selectedFundId);

      return matchesRole && matchesFund && matchesActivityFilter(user, activityFilter) && matchesSearch(user, searchQuery);
    });
  }, [users, selectedRoles, selectedFundId, activityFilter, searchQuery]);

  const hasActiveFilters = selectedRoles.length > 0 || selectedFundId !== null || activityFilter !== 'all' || searchQuery.trim() !== '';

  const resetFilters = useCallback(() => {
    setSelectedRoles([]);
    setSelectedFundId(null);
    setActivityFilter('all');
    setSearchQuery('');
  }, []);

  const askToLoginAs = useCallback((user: ManagedUserDtoIn) => {
    setLoginAsError(null);
    setUserToLoginAs(user);
  }, []);

  // The deleted user is gone from the list, so a details panel still showing them has to go too.
  const handleUserDeleted = useCallback(() => {
    setSelectedUser((current) => (current?.email === userToDelete?.email ? null : current));
    refetch();
  }, [refetch, userToDelete]);

  // The open details panel shows the edited user as saved - after an email change the old address
  // is gone from the list, and the panel would otherwise keep showing it.
  const handleUserEdited = useCallback(
    (updated: ManagedUserDtoIn) => {
      setSelectedUser((current) => (current && (current.email === userToEdit?.email || current.email === updated.email) ? updated : current));
      refetch();
    },
    [refetch, userToEdit]
  );

  const handleLoginAs = async () => {
    if (!userToLoginAs) return;

    setIsLoggingInAsUser(true);
    setLoginAsError(null);
    try {
      // loginAs reloads the app under the impersonated user, so there is nothing to navigate to
      // and nothing to reset here - the dialog stays in its loading state until the page is gone.
      await loginAs(userToLoginAs.email);
    } catch {
      setLoginAsError(t('usersManagement.loginAs.error'));
      setIsLoggingInAsUser(false);
    }
  };

  const columns: ColumnDef<ManagedUserDtoIn>[] = [
    {
      id: 'fullName',
      header: Col(t('usersManagement.columns.fullName')),
      enableSorting: true,
      accessorFn: (row) => getUserFullName(row),
      cell: ({ row }) => {
        const fullName = getUserFullName(row.original);
        return fullName ? <span className="body-lr-semibold">{fullName}</span> : <span className="text-(--font-body-3)">{t('usersManagement.noName')}</span>;
      },
      meta: { className: 'min-w-[160px]' },
    },
    {
      id: 'email',
      header: Col(t('usersManagement.columns.email')),
      accessorKey: 'email',
      enableSorting: true,
      cell: ({ row }) => (
        <span dir="ltr" className="block text-start">
          {row.original.email}
        </span>
      ),
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'phoneNumber',
      header: Col(t('usersManagement.columns.phoneNumber')),
      enableSorting: true,
      accessorFn: (row) => row.phoneNumber ?? '',
      cell: ({ row }) =>
        row.original.phoneNumber ? (
          <span dir="ltr" className="block text-start whitespace-nowrap">
            {row.original.phoneNumber}
          </span>
        ) : (
          <span className="text-(--font-body-3)">-</span>
        ),
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'roles',
      header: Col(t('usersManagement.columns.roles')),
      enableSorting: true,
      accessorFn: (row) => roleSortValue(row),
      cell: ({ row }) => <RoleChips roles={row.original.roles ?? []} />,
      // Inline padding beats the shared .td shorthand, and keeps the chips off the phone column
      meta: { className: 'min-w-[150px]', style: { paddingInlineStart: '28px' } },
    },
    {
      id: 'funds',
      header: Col(t('usersManagement.columns.funds')),
      enableSorting: true,
      accessorFn: (row) => (row.fundIds ?? []).length,
      cell: ({ row }) => {
        const fundIds = row.original.fundIds ?? [];
        if (fundIds.length === 0) return <span className="text-(--font-body-3)">-</span>;

        const hiddenCount = fundIds.length - MAX_VISIBLE_FUNDS;
        const tooltip = fundIds.map((id) => `${id} - ${fundNamesById.get(id) ?? ''}`.trim()).join(', ');

        return (
          <span className="flex items-center gap-1.5 whitespace-nowrap" title={tooltip}>
            <span>{fundIds.slice(0, MAX_VISIBLE_FUNDS).join(', ')}</span>
            {hiddenCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-(--surface-grey-low) text-(--font-body-1) body-xs-sm-regular">+{hiddenCount}</span>
            )}
          </span>
        );
      },
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'creationTime',
      header: Col(t('usersManagement.columns.registeredAt')),
      enableSorting: true,
      accessorFn: (row) => userDateSortValue(row.creationTime),
      cell: ({ row }) => {
        const formatted = formatUserDate(row.original.creationTime);
        return formatted ? <span className="whitespace-nowrap">{formatted}</span> : <span className="text-(--font-body-3)">{t('usersManagement.never')}</span>;
      },
      meta: { className: 'min-w-[150px]', hiddenOnMobile: true },
    },
  ];

  if (canManage) {
    columns.push({
      id: 'actions',
      // No header - the icons speak for themselves and each one carries a tooltip
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        const isSelf = user.email === currentUser?.email;

        // The row itself opens the details panel, so every action has to keep its click to itself.
        const runAction = (action: (user: ManagedUserDtoIn) => void) => (e: React.MouseEvent) => {
          e.stopPropagation();
          action(user);
        };

        const iconButton = (icon: string, label: string, onClick: (user: ManagedUserDtoIn) => void, disabled = false) => (
          <button type="button" className={styles.iconButton} title={label} aria-label={label} disabled={disabled} onClick={runAction(onClick)}>
            <TintedIcon icon={icon} size={20} />
          </button>
        );

        return (
          <div className={styles.rowActions}>
            {iconButton(editIcon, t('usersManagement.actions.edit'), setUserToEdit)}
            {iconButton(permissionsIcon, t('usersManagement.actions.permissions'), setUserToEditPermissions)}
            {iconButton(loginAsIcon, isSelf ? t('usersManagement.loginAs.selfDisabled') : t('usersManagement.actions.loginAs'), askToLoginAs, isSelf)}
            {iconButton(deleteIcon, isSelf ? t('usersManagement.delete.selfDisabled') : t('usersManagement.actions.delete'), setUserToDelete, isSelf)}
          </div>
        );
      },
      // A pinned narrow column, so the table's spare width goes to the data columns
      // and the icons end up right against the edge.
      meta: { className: 'min-w-[152px]', style: { width: '152px' }, lockLayout: true },
    });
  }

  return (
    <div className="w-full">
      {selectedUser && (
        <UserBlade
          key={selectedUser.email}
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          canManage={canManage}
          isSelf={selectedUser.email === currentUser?.email}
          onEditDetails={() => setUserToEdit(selectedUser)}
          onEditPermissions={() => setUserToEditPermissions(selectedUser)}
          onLoginAs={() => askToLoginAs(selectedUser)}
          onDelete={() => setUserToDelete(selectedUser)}
        />
      )}

      <DataTable
        title={t('usersManagement.title')}
        data={filteredUsers}
        isLoading={isLoading}
        columns={columns}
        sortBy={{ id: 'creationTime', desc: true }}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="usersManagement"
        searchPlaceholder="usersManagement.search"
        searchTooltip="usersManagement.searchTooltip"
        handleRowClick={setSelectedUser}
        activeRecord={selectedUser ?? undefined}
        filters={
          <UsersFiltersArea
            selectedRoles={selectedRoles}
            onRolesChange={setSelectedRoles}
            selectedFundId={selectedFundId}
            onFundChange={setSelectedFundId}
            activityFilter={activityFilter}
            onActivityChange={setActivityFilter}
            roleCounts={roleCounts}
            onResetFilters={resetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        }
        emptyStateContent={{
          title: t('usersManagement.emptyTitle'),
          description: t('usersManagement.emptyDescription'),
        }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={() => true}
      />

      {userToEdit && (
        <EditUserDetailsDialog
          open={!!userToEdit}
          user={userToEdit}
          isSelf={userToEdit.email === currentUser?.email}
          onClose={() => setUserToEdit(null)}
          onSuccess={handleUserEdited}
        />
      )}

      {userToEditPermissions && (
        <EditUserPermissionsDialog
          open={!!userToEditPermissions}
          user={userToEditPermissions}
          onClose={() => setUserToEditPermissions(null)}
          onSuccess={refetch}
        />
      )}

      {userToDelete && <DeleteUserDialog open={!!userToDelete} user={userToDelete} onClose={() => setUserToDelete(null)} onSuccess={handleUserDeleted} />}

      {userToLoginAs && (
        <CompactDialog
          open={!!userToLoginAs}
          onClose={() => setUserToLoginAs(null)}
          title={t('usersManagement.loginAs.title')}
          narrow
          footer={
            <>
              <Button variant="white" onClick={() => setUserToLoginAs(null)} disabled={isLoggingInAsUser}>
                {t('cancel')}
              </Button>
              <Button variant="primary" onClick={handleLoginAs} disabled={isLoggingInAsUser}>
                {isLoggingInAsUser ? t('loading') : t('usersManagement.actions.loginAs')}
              </Button>
            </>
          }
        >
          <p className="body-md-regular text-(--font-body-1)">
            {t('usersManagement.loginAs.confirm', { name: getUserFullName(userToLoginAs) || userToLoginAs.email })}
          </p>
          <span dir="ltr" className="body-sm-regular text-(--font-body-2) text-start">
            {userToLoginAs.email}
          </span>
          <p className="body-sm-regular text-(--font-body-2)">{t('usersManagement.loginAs.note')}</p>
          {loginAsError && <p className="text-red-500 body-sm-regular">{loginAsError}</p>}
        </CompactDialog>
      )}
    </div>
  );
};

export default UsersManagementPage;
